import * as React from "react";

import {
  Alert,
  Button,
  FormField,
  Input,
  notify,
  RadioGroup,
  type RadioOption,
  Spinner,
  Textarea,
} from "Components/ui";
import { CalendarClock } from "Components/ui/icons";
import {
  LineItemInput,
  ScheduleInput,
  SchedulePreviewInput,
  ScheduleUpdateInput,
} from "api/billing";
import { getApiErrorMessage } from "api/apiError";
import { addDaysToDate, formatDate, formatINR, isDateOnly, todayIST } from "utils/format";
import DialogShell from "Routes/Billing/Components/DialogShell";
import { DateInput } from "Routes/Billing/Components/ui";
import { closeAfterFlush, useDebouncedValue } from "Routes/Billing/hooks";
import { useScheduleActions, useSchedulePreview } from "../data";
import { LineItemDraft, ScheduleEditorState } from "../types";
import { draftsFromItems, newLineItem, validateLineItems } from "../lineItems";
import { CADENCE_PRESETS, cadenceText, dueDayText } from "../cadence";
import LineItemsEditor from "./LineItemsEditor";

type PresetId = "1" | "3" | "6" | "12" | "custom";

interface ScheduleEditorModalProps {
  tenantId: string;
  state: ScheduleEditorState;
  warningDays: number;
  onClose: () => void;
}

const DEFAULT_ISSUE_DAYS_BEFORE = 10;

const CADENCE_OPTIONS: RadioOption<PresetId>[] = [
  ...CADENCE_PRESETS.map((preset) => ({ value: preset.id, label: preset.label })),
  { value: "custom", label: "Custom" },
];

const presetFor = (months: number): PresetId => {
  const match = CADENCE_PRESETS.find((preset) => preset.months === months);
  return match ? match.id : "custom";
};

const toComparableItems = (items: LineItemInput[]) =>
  JSON.stringify(items.map((item) => ({ d: item.description, q: item.qty, p: item.unitPrice })));

/** Add / edit a recurring charge with presets, line items and a live preview of the next dues. */
const ScheduleEditorModal: React.FC<ScheduleEditorModalProps> = ({
  tenantId,
  state,
  warningDays,
  onClose,
}) => {
  const schedule = state.mode === "edit" ? state.schedule : null;
  const started = Boolean(schedule && schedule.nextPeriodIndex > 0);
  const initialDate = schedule
    ? started && schedule.nextPeriod
      ? schedule.nextPeriod.dueDate
      : schedule.firstDueDate
    : "";
  const today = todayIST();

  const [name, setName] = React.useState<string>(schedule ? schedule.name : "TeleOpd subscription");
  const [preset, setPreset] = React.useState<PresetId>(
    schedule ? presetFor(schedule.intervalMonths) : "1"
  );
  const [customMonths, setCustomMonths] = React.useState<string>(
    schedule && presetFor(schedule.intervalMonths) === "custom"
      ? String(schedule.intervalMonths)
      : "2"
  );
  const [items, setItems] = React.useState<LineItemDraft[]>(
    schedule ? draftsFromItems(schedule.items) : [newLineItem()]
  );
  const [firstDueDate, setFirstDueDate] = React.useState<string>(initialDate);
  const [endDate, setEndDate] = React.useState<string>(schedule?.endDate || "");
  const [issueDaysBefore, setIssueDaysBefore] = React.useState<string>(
    String(schedule ? schedule.issueDaysBefore : Math.max(DEFAULT_ISSUE_DAYS_BEFORE, warningDays))
  );
  const [notes, setNotes] = React.useState<string>(schedule?.notes || "");
  const [showErrors, setShowErrors] = React.useState<boolean>(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState<boolean>(false);

  const actions = useScheduleActions(tenantId);

  const intervalMonths = preset === "custom" ? Number(customMonths.trim()) : Number(preset);
  const intervalValid =
    Number.isInteger(intervalMonths) && intervalMonths >= 1 && intervalMonths <= 24;
  const issueDays = Number(issueDaysBefore.trim());
  const issueDaysValid = /^\d+$/.test(issueDaysBefore.trim()) && issueDays >= 3 && issueDays <= 90;
  const lineValidation = validateLineItems(items);
  const dateChanged = firstDueDate !== initialDate;
  const cadenceChanged = Boolean(schedule && intervalMonths !== schedule.intervalMonths);
  // Started schedules re-anchor at the next period, so the date is sent whenever the cadence or
  // the date changes (keeps the result identical to the preview).
  const sendsDate = !schedule || dateChanged || (started && cadenceChanged);
  const showDateError = showErrors || (Boolean(firstDueDate) && sendsDate);

  const errors = {
    name: !name.trim()
      ? "Give the charge a name."
      : name.trim().length > 120
      ? "Keep the name under 120 characters."
      : null,
    interval: intervalValid ? null : "Choose 1 to 24 months.",
    firstDueDate: !isDateOnly(firstDueDate)
      ? "Pick a date."
      : sendsDate && firstDueDate < today
      ? "The date can't be in the past. Pick today or later."
      : null,
    endDate:
      endDate && isDateOnly(firstDueDate) && endDate < firstDueDate
        ? "The end date must be on or after the first due date."
        : null,
    issueDaysBefore: !issueDaysValid
      ? "Use 3 to 90 days."
      : issueDays < warningDays
      ? `Must be at least ${warningDays} days (the warning period).`
      : null,
    notes: notes.length > 1000 ? "Keep notes under 1000 characters." : null,
  };
  const hasErrors = Object.values(errors).some(Boolean) || !lineValidation.items;

  // Live preview of the next three dues (debounced).
  const previewInput: SchedulePreviewInput | null =
    isDateOnly(firstDueDate) && intervalValid
      ? {
          firstDueDate,
          intervalMonths,
          items: lineValidation.items || undefined,
          endDate: endDate && isDateOnly(endDate) ? endDate : null,
          count: 3,
          issueDaysBefore: issueDaysValid ? issueDays : undefined,
        }
      : null;
  const previewKey = previewInput ? JSON.stringify(previewInput) : "";
  const debouncedKey = useDebouncedValue(previewKey, 400);
  const debouncedInput = React.useMemo<SchedulePreviewInput | null>(
    () => (debouncedKey ? (JSON.parse(debouncedKey) as SchedulePreviewInput) : null),
    [debouncedKey]
  );
  const preview = useSchedulePreview(debouncedInput);
  const previewPending = previewKey !== debouncedKey || preview.isFetching;

  const buildUpdate = (validItems: LineItemInput[]): ScheduleUpdateInput => {
    if (!schedule) {
      return {};
    }
    const update: ScheduleUpdateInput = {};
    if (name.trim() !== schedule.name) {
      update.name = name.trim();
    }
    if (toComparableItems(validItems) !== toComparableItems(schedule.items)) {
      update.items = validItems;
    }
    if (cadenceChanged) {
      update.intervalMonths = intervalMonths;
    }
    if (sendsDate) {
      update.firstDueDate = firstDueDate;
    }
    if ((endDate || null) !== (schedule.endDate || null)) {
      update.endDate = endDate || null;
    }
    if (issueDays !== schedule.issueDaysBefore) {
      update.issueDaysBefore = issueDays;
    }
    if ((notes.trim() || null) !== (schedule.notes || null)) {
      update.notes = notes.trim() || null;
    }
    return update;
  };

  const handleSave = async () => {
    setShowErrors(true);
    setSubmitError(null);
    if (hasErrors || !lineValidation.items) {
      return;
    }
    setSaving(true);
    try {
      if (schedule) {
        const update = buildUpdate(lineValidation.items);
        if (Object.keys(update).length === 0) {
          notify.info("Nothing changed");
          onClose();
          return;
        }
        await actions.update.mutateAsync({ scheduleId: schedule._id, input: update });
      } else {
        const input: ScheduleInput = {
          name: name.trim(),
          items: lineValidation.items,
          intervalMonths,
          firstDueDate,
          endDate: endDate || null,
          issueDaysBefore: issueDays,
          notes: notes.trim() || null,
        };
        await actions.create.mutateAsync(input);
      }
      closeAfterFlush(onClose);
    } catch (err) {
      setSubmitError(getApiErrorMessage(err));
      setSaving(false);
    }
  };

  const summary =
    intervalValid && isDateOnly(firstDueDate)
      ? `${formatINR(lineValidation.total)} ${cadenceText(intervalMonths)}, ${dueDayText(
          firstDueDate,
          intervalMonths
        )}`
      : null;

  return (
    <DialogShell
      title={schedule ? "Edit recurring charge" : "Add recurring charge"}
      subtitle={
        schedule
          ? "Changes apply to periods that are not invoiced yet."
          : "SAP issues an invoice before every due date and emails reminders."
      }
      size="lg"
      onClose={saving ? () => undefined : onClose}
      closeOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            {schedule ? "Save changes" : "Add recurring charge"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <FormField
          label="Name"
          id="schedule-name"
          required
          error={(showErrors && errors.name) || undefined}
        >
          <Input
            type="text"
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
          />
        </FormField>

        <FormField
          label="How often"
          id="schedule-cadence"
          error={errors.interval || undefined}
          hint={
            started && schedule && intervalMonths !== schedule.intervalMonths
              ? "Invoices already issued keep their dates; the new cadence starts from the next due date below."
              : undefined
          }
        >
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <RadioGroup
              options={CADENCE_OPTIONS}
              value={preset}
              onValueChange={setPreset}
              aria-label="How often"
              orientation="horizontal"
              className="flex flex-wrap gap-x-5 gap-y-2"
            />
            {preset === "custom" && (
              <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                every
                <Input
                  id="schedule-custom-months"
                  type="text"
                  inputMode="numeric"
                  value={customMonths}
                  onChange={(event) => setCustomMonths(event.target.value)}
                  aria-label="Months between charges"
                  wrapperClassName="w-16"
                  className="text-center tabular-nums"
                />
                months
              </span>
            )}
          </div>
        </FormField>

        <div>
          <p className="mb-2 text-sm font-medium text-foreground">Line items</p>
          <LineItemsEditor
            items={items}
            onChange={setItems}
            errors={lineValidation.errors}
            total={lineValidation.total}
            totalError={lineValidation.totalError}
            showErrors={showErrors}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FormField
            label={started ? "Next due date" : "First due date"}
            id="schedule-first-due"
            required
            error={(showDateError && errors.firstDueDate) || undefined}
            hint={started ? "Re-anchors future periods." : "Today or later."}
          >
            <DateInput
              id="schedule-first-due"
              value={firstDueDate}
              min={today}
              onChange={setFirstDueDate}
              hasError={Boolean(showDateError && errors.firstDueDate)}
            />
          </FormField>
          <FormField
            label="End date"
            id="schedule-end"
            error={errors.endDate || undefined}
            hint={
              endDate ? (
                <Button variant="link" size="xs" onClick={() => setEndDate("")}>
                  Clear (runs until ended)
                </Button>
              ) : (
                "Optional. No dues after this date."
              )
            }
          >
            <DateInput
              id="schedule-end"
              value={endDate}
              min={isDateOnly(firstDueDate) ? firstDueDate : today}
              onChange={setEndDate}
              hasError={Boolean(errors.endDate)}
            />
          </FormField>
          <FormField
            label="Issue invoice (days before due)"
            id="schedule-issue-days"
            error={errors.issueDaysBefore || undefined}
            hint={`At least ${warningDays}, the warning period, so the invoice exists before warnings start.`}
          >
            <Input
              type="text"
              inputMode="numeric"
              value={issueDaysBefore}
              onChange={(event) => setIssueDaysBefore(event.target.value)}
              className="tabular-nums"
            />
          </FormField>
        </div>

        <FormField
          label="Notes"
          id="schedule-notes"
          error={errors.notes || undefined}
          hint="Copied to every invoice this charge creates (internal)."
        >
          <Textarea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </FormField>

        <section
          aria-label="Next dues"
          className="rounded-card border border-border bg-surface-muted/60 px-4 py-3"
        >
          <div className="flex items-center justify-between gap-2">
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <CalendarClock className="h-4 w-4 text-primary-text" aria-hidden="true" />
              Next dues
            </p>
            {previewInput && previewPending && (
              <span className="text-muted-foreground">
                <Spinner />
              </span>
            )}
          </div>
          {summary && <p className="mt-1 text-sm text-foreground">{summary}.</p>}
          {!previewInput ? (
            <p className="mt-2 text-sm text-muted-foreground">
              Pick a cadence and a first due date to see the next dues.
            </p>
          ) : preview.isError ? (
            <p className="mt-2 text-sm text-danger-text">{getApiErrorMessage(preview.error)}</p>
          ) : preview.data && preview.data.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">No dues fall before the end date.</p>
          ) : (
            <ol className="mt-2 space-y-1.5">
              {(preview.data || []).map((period) => (
                <li key={period.periodIndex} className="text-sm text-foreground">
                  <span className="font-semibold tabular-nums">
                    {formatINR(lineValidation.items ? period.total : lineValidation.total)}
                  </span>{" "}
                  due on <span className="font-semibold">{formatDate(period.dueDate)}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    · invoice goes out {formatDate(period.issueDate)}, warnings from{" "}
                    {formatDate(addDaysToDate(period.dueDate, -warningDays))}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>

        {submitError && (
          <Alert tone="danger" title="Not saved">
            {submitError}
          </Alert>
        )}
      </div>
    </DialogShell>
  );
};

export default ScheduleEditorModal;
